import type { WritableSignal } from '@angular/core';
import { effect, inject, signal } from '@angular/core';
import type { CanMatchFn } from '@angular/router';
import { type GuardResult, Router } from '@angular/router';

import type { ITenantContext } from '#/app/shared/services/tenant.context';
import { TenantContext } from '#/app/shared/services/tenant.context';

export const tenantMembershipGuard = (
  redirectToNoAccess = true,
): CanMatchFn => {
  return () => {
    const router = inject(Router);
    const tenantContext = inject(TenantContext);
    const reloadTenantsListInitiated = signal(false);

    // -------------------------------------------------------
    // FIXME: To be removed once Angular v21.2 is released
    // Get the full URL from current navigation
    const navigation = router.currentNavigation();
    const fullUrl = navigation?.extractedUrl.toString() ?? '';

    // Use matcher to extract parameters from the full URL
    const urlTree = router.parseUrl(fullUrl);
    const primarySegmentGroup = urlTree.root.children['primary'];
    const allSegments = primarySegmentGroup.segments;

    // Match against the parent path pattern to extract tenantId
    // Pattern: /o/:tenantId/...
    const tenantIdParam =
      allSegments.length > 1 && allSegments[0].path === 'o'
        ? allSegments[1].path
        : '';
    //-------------------------------------------------------

    return tenantMembershipGuardResult(
      tenantContext,
      tenantIdParam,
      reloadTenantsListInitiated,
      router,
      redirectToNoAccess,
    );
  };
};

function tenantMembershipGuardResult(
  tenantContext: ITenantContext,
  tenantIdParam: string | null,
  reloadTenantsListInitiated: WritableSignal<boolean>,
  router: Router,
  redirectToNoAccess: boolean,
) {
  return new Promise<GuardResult>((resolve) => {
    const effectRef = effect(
      () => {
        if (!tenantContext.hasTenantsLoaded()) {
          if (tenantContext.tenants.status() === 'idle') {
            resolve(false);
          }

          return;
        }

        if (tenantContext.tenants.hasValue()) {
          const tenants = tenantContext.tenants.value();
          if (tenantIdParam) {
            const isMember = tenants.some(
              (tenant) => tenant.tenantId === tenantIdParam,
            );
            if (isMember) {
              tenantContext.currentTenantId.set(tenantIdParam);
              effectRef.destroy();
              resolve(true);
            } else {
              // Attempt to reload the list just once, to check again (e.g. just added). Otherwise redirect to no access page.
              if (!reloadTenantsListInitiated()) {
                tenantContext.tenants.reload();
                reloadTenantsListInitiated.set(true);
              } else {
                effectRef.destroy();
                if (redirectToNoAccess) {
                  resolve(
                    router.createUrlTree([`/o/${tenantIdParam}/no-access`], {
                      queryParams: { reason: 'unauthorized' },
                    }),
                  );
                }
                resolve(false);
              }
            }
          } else {
            throw new Error('tenantMembershipGuard requires a tenantId');
          }
        }
      },
      { manualCleanup: true },
    );
  });
}
